import { useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';

import { ItemFormData } from '@/components/ItemModal/useItemModal';
import { AttachmentData } from '@/components/AttachmentModal/useAttachmentModal';
import { ProponentFormData } from '@/components/ProponentModal/useProponentModal';

import {
  CreateInspectionRequestPayload,
  InspectionItemPayload,
  Proponent as ApiProponent,
  Phone,
  Attachment as ApiAttachment,
  Contact,
  DuplicateInspection,
} from '@/services/types/inspection.types';
import { useAlertContext } from '@/contexts/alert.context';
import { useErrorHandler } from '@/shared/hooks/useErrorHandler';
import { inspectionService } from '@/services/inspection.service';
import { docmanService } from '@/services/docman.service';

export interface Proponent {
  id: string;
  proponentName: string;
  document: string;
  email: string;
  phones: { phoneType: string; phone: string }[];
}

export interface InspectionItem {
  id: string;
  count: number;
  productName: string;
  data: string;
  address: string;
  rawData: ItemFormData;
}

export interface Attachment {
  id: string;
  name: string;
  description: string;
  size: string;
  type: string;
  filePath?: string;
  file?: File;
}

export interface InspectionFormData {
  channel: string;
  proposalNumber?: string;
  limit?: string;
}

export function useRequestInspection() {
  const [proponents, setProponents] = useState<Proponent[]>([]);
  const [items, setItems] = useState<InspectionItem[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isProponentModalOpen, setIsProponentModalOpen] = useState(false);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingProponent, setEditingProponent] = useState<Proponent | null>(null);
  const [editingItem, setEditingItem] = useState<InspectionItem | null>(null);
  const [editingAttachment, setEditingAttachment] = useState<Attachment | null>(null);

  // Estado para modal de duplicação
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicateInspections, setDuplicateInspections] = useState<DuplicateInspection[]>([]);
  const [pendingItemData, setPendingItemData] = useState<ItemFormData | null>(null);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);

  const navigation = useNavigate();
  const { notify } = useAlertContext();
  const { handleError } = useErrorHandler();

  const handleNewProponent = () => {
    setIsProponentModalOpen(true);
  };

  const handleCloseProponentModal = () => {
    setIsProponentModalOpen(false);
    setEditingProponent(null);
  };

  const handleSubmitProponent = (data: ProponentFormData) => {
    if (editingProponent) {
      const updatedProponent: Proponent = {
        ...editingProponent,
        proponentName: data.name,
        document: data.document || '-',
        email: data.email || '-',
        phones: data.phones || [],
      };
      setProponents(proponents.map((p) => (p.id === editingProponent.id ? updatedProponent : p)));
      setEditingProponent(null);
    } else {
      const newProponent: Proponent = {
        id: Math.random().toString(36).substring(2, 11),
        proponentName: data.name,
        document: data.document || '-',
        email: data.email || '-',
        phones: data.phones || [],
      };
      setProponents([...proponents, newProponent]);
    }
  };

  const handleEditProponent = (proponent: Proponent) => {
    setEditingProponent(proponent);
    setIsProponentModalOpen(true);
  };

  const handleDeleteProponent = (id: string) => {
    setProponents(proponents.filter((p) => p.id !== id));
  };

  const handleNewItem = () => {
    setIsItemModalOpen(true);
  };

  const handleCloseItemModal = () => {
    setIsItemModalOpen(false);
    setEditingItem(null);
  };

  const addItemToList = (data: ItemFormData) => {
    if (editingItem) {
      const updatedItem: InspectionItem = {
        ...editingItem,
        productName: `${data.inspectionTypeLabel || data.inspectionType}`,
        data: `${data.inspectionTypeLabel || data.inspectionType} - ${data.propertyTypeLabel || data.propertyType}`,
        address: `${data.street}, ${data.number} - ${data.district}, ${data.city}/${data.state}`,
        rawData: data,
      };
      setItems(items.map((item) => (item.id === editingItem.id ? updatedItem : item)));
      setEditingItem(null);
    } else {
      const newItem: InspectionItem = {
        id: Math.random().toString(36).substring(2, 11),
        count: items.length + 1,
        productName: `${data.inspectionTypeLabel || data.inspectionType}`,
        data: `${data.inspectionTypeLabel || data.inspectionType} - ${data.propertyTypeLabel || data.propertyType}`,
        address: `${data.street}, ${data.number} - ${data.district}, ${data.city}/${data.state}`,
        rawData: data,
      };
      setItems([...items, newItem]);
    }
  };

  const handleSubmitItem = async (data: ItemFormData) => {
    // Verifica duplicação de localização
    setIsCheckingDuplicate(true);
    try {
      const response = await inspectionService.checkDuplicateLocation({
        street: data.street,
        number: data.number || '',
        district: data.district,
        city: data.city,
        state: data.state,
        cep: data.postalCode,
      });

      if (response.hasDuplicate && response.duplicateInspections.length > 0) {
        // Encontrou duplicação - guarda os dados e abre o modal
        setPendingItemData(data);
        setDuplicateInspections(response.duplicateInspections);
        setIsDuplicateModalOpen(true);
        // Lança exceção para impedir que o modal feche
        throw new Error('DUPLICATE_LOCATION');
      } else {
        // Sem duplicação - adiciona direto
        addItemToList(data);
      }
    } catch (error: any) {
      // Se for erro de duplicação, não exibe mensagem de erro
      if (error?.message === 'DUPLICATE_LOCATION') {
        throw error; // Re-lança para o modal não fechar
      }
      // Em caso de outro erro na verificação, adiciona o item mesmo assim
      addItemToList(data);
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  const handleDuplicateConfirm = () => {
    // Usuário confirmou que quer adicionar mesmo com duplicação
    if (pendingItemData) {
      addItemToList(pendingItemData);
    }
    setIsDuplicateModalOpen(false);
    setPendingItemData(null);
    setDuplicateInspections([]);
    // Fecha o modal do item
    setIsItemModalOpen(false);
    setEditingItem(null);
  };

  const handleDuplicateCancel = () => {
    // Usuário cancelou - reabre o modal de item para editar
    setIsDuplicateModalOpen(false);
    setDuplicateInspections([]);
    // Limpa os dados pendentes mas reabre o modal de item
    setPendingItemData(null);
    setIsItemModalOpen(true);
  };

  const handleCloseDuplicateModal = () => {
    setIsDuplicateModalOpen(false);
    setPendingItemData(null);
    setDuplicateInspections([]);
  };

  const handleEditItem = (item: InspectionItem) => {
    setEditingItem(item);
    setIsItemModalOpen(true);
  };

  const handleDeleteItem = (id: string) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const handleNewAttachment = () => {
    setIsAttachmentModalOpen(true);
  };

  const handleCloseAttachmentModal = () => {
    setIsAttachmentModalOpen(false);
    setEditingAttachment(null);
  };

  const handleSubmitAttachment = (data: AttachmentData) => {
    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    if (editingAttachment) {
      const updatedAttachment: Attachment = {
        ...editingAttachment,
        name: data.fileName,
        description: data.description,
        size: formatFileSize(data.fileSize),
        type: data.fileType || 'Arquivo',
        filePath: data.filePath,
        file: data.file,
      };
      setAttachments(
        attachments.map((att) => (att.id === editingAttachment.id ? updatedAttachment : att))
      );
      setEditingAttachment(null);
    } else {
      const newAttachment: Attachment = {
        id: data.id,
        name: data.fileName,
        description: data.description,
        size: formatFileSize(data.fileSize),
        type: data.fileType || 'Arquivo',
        filePath: data.filePath,
        file: data.file,
      };
      setAttachments([...attachments, newAttachment]);
    }
  };

  const handleEditAttachment = (attachment: Attachment) => {
    setEditingAttachment(attachment);
    setIsAttachmentModalOpen(true);
  };

  const handleDeleteAttachment = (id: string) => {
    setAttachments(attachments.filter((att) => att.id !== id));
  };

  const handleSubmit = async (values: InspectionFormData) => {
    setIsSubmitting(true);
    try {
      const apiProponents: ApiProponent[] = proponents.map((proponent) => {
        const phones: Phone[] = proponent.phones.map((phone) => ({
          phoneNumber: phone.phone,
          phoneType: phone.phoneType || 'Celular',
        }));

        return {
          name: proponent.proponentName,
          cpfCnpj: proponent.document,
          email: proponent.email,
          phones,
        };
      });

      const apiItems: InspectionItemPayload[] = items.map((item) => {
        const rawData = item.rawData;

        const contacts: Contact[] | undefined =
          rawData.contacts && rawData.contacts.length > 0
            ? rawData.contacts.map((contact) => ({
                contactName: contact.contactName,
                email: contact.email,
                contactType: Number(contact.contactType),
                ...(contact.mobilePhone && { mobilePhone: contact.mobilePhone }),
                ...(contact.residentialPhone && { residentialPhone: contact.residentialPhone }),
                ...(contact.commercialPhone && { commercialPhone: contact.commercialPhone }),
              }))
            : undefined;

        return {
          inspectionReasonId: rawData.inspectionReason,
          inspectionTypeId: rawData.inspectionType,
          propertyTypeId: rawData.propertyType,
          ...(rawData.observation && { observation: rawData.observation }),
          cep: rawData.postalCode,
          street: rawData.street,
          ...(rawData.complement && { complement: rawData.complement }),
          number: rawData.number || '',
          district: rawData.district,
          state: rawData.state,
          city: rawData.city,
          ...(rawData.landArea && { landArea: Number(rawData.landArea) }),
          ...(rawData.builtArea && { builtArea: Number(rawData.builtArea) }),
          ...(rawData.privateArea && { privateArea: Number(rawData.privateArea) }),
          ...(rawData.iptuNumber && { iptuNumber: rawData.iptuNumber }),
          ...(rawData.registrationNumber && { registrationNumber: rawData.registrationNumber }),
          ...(rawData.registryOfficeNumber && { registryOfficeNumber: rawData.registryOfficeNumber }),
          ...(contacts && { contacts }),
          isAutomaticReport: rawData.isAutomaticReport ?? false,
        };
      });

      const uploadedAttachments: ApiAttachment[] = [];

      for (const attachment of attachments) {
        if (attachment.file) {
          try {
            const tempEntityId = `temp-${Date.now()}`;

            const uploadResult = await docmanService.uploadFile(
              tempEntityId,
              attachment.file,
              'OUTROS',
            );

            uploadedAttachments.push({
              description: attachment.description,
              fileName: attachment.name,
              fileSize: attachment.file.size,
              fileType: attachment.file.type || 'application/octet-stream',
              filePath: `docman/${uploadResult.documentId}`,
            });
          } catch {
            throw new Error(`Falha ao enviar anexo "${attachment.name}"`);
          }
        }
      }

      const apiAttachments = uploadedAttachments;

      const payload: CreateInspectionRequestPayload = {
        proposalNumber: values.proposalNumber ?? '',
        channel: values.channel,
        proponents: apiProponents,
        items: apiItems,
        ...(apiAttachments.length > 0 && { attachments: apiAttachments }),
      };

      await inspectionService.createInspectionRequest(payload);

      setProponents([]);
      setItems([]);
      setAttachments([]);

      notify({ message: 'Inspeção solicitada com sucesso!', type: 'success' });

      navigation('/');
    } catch (error) {
      handleError(error, 'Erro ao solicitar inspeção');
    } finally {
      setIsSubmitting(false);
    }
  };

  const proponentColumns: ColumnsType<Proponent> = [
    {
      key: 'proponent_name',
      title: 'Proponente',
      dataIndex: 'proponentName',
    },
    {
      key: 'document',
      title: 'CPF/CNPJ',
      dataIndex: 'document',
    },
    {
      key: 'email',
      title: 'E-mail',
      dataIndex: 'email',
    },
    {
      key: 'phone',
      title: 'Telefones',
      dataIndex: 'phones',
      render: (phones: { phoneType: string; phone: string }[]) => {
        if (!phones || phones.length === 0) return '-';        
        return phones
          .map((phone) => `${phone.phone} (${phone.phoneType === 'Celular' ? 'Móvel' : 'Fixo'})`)
          .join(', ');
      },
    },
    {
      key: 'actions',
      title: 'Ações',
      render: (_: any, record: Proponent) =>
        ({
          handleEdit: () => handleEditProponent(record),
          handleDelete: () => handleDeleteProponent(record.id),
        }) as any,
    },
  ];

  const itemColumns = [
    {
      key: 'count',
      title: '#',
      dataIndex: 'count',
    },
    {
      key: 'product_name',
      title: 'Produto',
      dataIndex: 'productName',
      render: (text: string, record: InspectionItem) => {
        const isAuto = record.rawData?.isAutomaticReport;
        if (isAuto) {
          return `${text} 🤖`;
        }
        return text;
      },
    },
    {
      key: 'data',
      title: 'Dados do Item',
      dataIndex: 'data',
    },
    {
      key: 'address',
      title: 'Endereço',
      dataIndex: 'address',
    },
    {
      key: 'actions',
      title: 'Ações',
      render: (_: any, record: any) =>
        ({
          handleEdit: () => handleEditItem(record as InspectionItem),
          handleDelete: () => handleDeleteItem((record as InspectionItem).id),
        }) as any,
    },
  ];

  const attachmentColumns = [
    {
      key: 'name',
      title: 'Nome',
      dataIndex: 'name',
    },
    {
      key: 'description',
      title: 'Descrição',
      dataIndex: 'description',
    },
    {
      key: 'size',
      title: 'Tamanho',
      dataIndex: 'size',
    },
    {
      key: 'type',
      title: 'Tipo',
      dataIndex: 'type',
    },
    {
      key: 'actions',
      title: 'Ações',
      render: (_: any, record: any) =>
        ({
          handleEdit: () => handleEditAttachment(record as Attachment),
          handleDelete: () => handleDeleteAttachment((record as Attachment).id),
        }) as any,
    },
  ];

  return {
    items,
    proponents,
    attachments,
    itemColumns,
    editingItem,
    isSubmitting,
    isItemModalOpen,
    editingProponent,
    proponentColumns,
    editingAttachment,
    attachmentColumns,
    isProponentModalOpen,
    isAttachmentModalOpen,
    // Duplicação de localização
    isDuplicateModalOpen,
    duplicateInspections,
    pendingItemData,
    isCheckingDuplicate,
    handleSubmit,
    handleNewItem,
    handleSubmitItem,
    handleNewProponent,
    handleNewAttachment,
    handleCloseItemModal,
    handleSubmitProponent,
    handleSubmitAttachment,
    handleCloseProponentModal,
    handleCloseAttachmentModal,
    // Duplicação de localização
    handleDuplicateConfirm,
    handleDuplicateCancel,
    handleCloseDuplicateModal,
  };
}
